import React, { useMemo } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Building, Mail, Pencil, Phone } from 'lucide-react';
import { getHoaSummaryDisplayPhoneEmail } from '../../../HoaDirectory/HoaDirectoryHelper';
import { formatPhoneNumber } from '../../../hoaForm/FormattedPhoneField';

const displayValue = v => {
  if (v === null || v === undefined || v === '') return 'N/A';
  return String(v);
};

const displayPhone = v => {
  if (v === null || v === undefined || v === '') return 'N/A';
  const formatted = formatPhoneNumber(v);
  return formatted || String(v);
};

const DirectoryInfo = ({ name, phone, email }) => (
  <>
    <div className="d-flex align-items-center mb-2">
      <span className="d-inline-flex align-items-center justify-content-center mr-2 flex-shrink-0 text-muted">
        <Building size={14} />
      </span>
      <p className="mb-0 midFont font-weight-medium flex-grow-1 min-w-0" style={{ wordBreak: 'break-all' }}>
        {displayValue(name)}
      </p>
    </div>
    <div className="d-flex flex-wrap align-items-center justify-content-start">
      <div className="d-flex align-items-center mr-3 mb-2 min-w-0">
        <span className="d-inline-flex align-items-center justify-content-center mr-2 flex-shrink-0 text-muted">
          <Phone size={14} />
        </span>
        <p className="mb-0 midFont font-weight-medium min-w-0" style={{ wordBreak: 'break-all' }}>
          {displayPhone(phone)}
        </p>
      </div>
      <div className="d-flex align-items-center mb-2 min-w-0 flex-grow-1">
        <span className="d-inline-flex align-items-center justify-content-center mr-2 flex-shrink-0 text-muted">
          <Mail size={14} />
        </span>
        <p className="mb-0 midFont font-weight-medium min-w-0" style={{ wordBreak: 'break-all' }}>
          {displayValue(email)}
        </p>
      </div>
    </div>
  </>
);

/**
 * HOA summary for the estoppel report builder.
 * Left: association. Right: management company (or Self-managed).
 * Edit applies draft-only changes until report save/preview syncs to association.
 */
const EstoppelReportHoaSummaryPanel = ({
  hoaSlot = {},
  canEdit = false,
  onEdit,
  explanation = '',
  onExplanationChange,
  showExplanationField = false,
  canEditExplanation = false,
  disabled = false
}) => {
  const isSelfManaged = hoaSlot.managementType === 'self';
  const { associationPhone, associationEmail, companyPhone, companyEmail } = useMemo(
    () => getHoaSummaryDisplayPhoneEmail(hoaSlot),
    [hoaSlot]
  );

  return (
    <div
      className="mb-3 px-3 py-2 border border-primary bg-yellow-light position-relative"
      style={{ borderRadius: '10px' }}
    >
      {canEdit && (
        <button
          type="button"
          className="btn btn-info btn-sm d-inline-flex align-items-center"
          style={{ position: 'absolute', top: 8, right: 12, zIndex: 1 }}
          onClick={onEdit}
          disabled={disabled}
          aria-label="Edit HOA"
        >
          <Pencil size={14} className="mr-1" />
          Edit
        </button>
      )}

      <Row>
        <Col xs={12} md={6} className="mb-2 mb-md-0">
          <p className="text-muted midFont mb-2 font-weight-medium">Association</p>
          <DirectoryInfo
            name={hoaSlot.name}
            phone={associationPhone}
            email={associationEmail}
          />
        </Col>
        <Col xs={12} md={6}>
          <p className="text-muted midFont mb-2 font-weight-medium">Management Company</p>
          {isSelfManaged ? (
            <p className="text-muted mb-0 largeFont font-italic">Self-managed</p>
          ) : (
            <DirectoryInfo
              name={hoaSlot.managementCompany}
              phone={companyPhone}
              email={companyEmail}
            />
          )}
        </Col>
      </Row>

      {showExplanationField && (
        <div className="mt-3 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.08)' }}>
          <Form.Group className="mb-0">
            <Form.Label className="midFont text-muted mb-1">
              No estoppel needed explanation
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={explanation ?? ''}
              onChange={e => onExplanationChange?.(e.target.value)}
              disabled={disabled || !canEditExplanation}
              placeholder="Optional explanation (shown on summary report)"
            />
          </Form.Group>
        </div>
      )}
    </div>
  );
};

export default EstoppelReportHoaSummaryPanel;
